import { FC, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import Image from 'next/image';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { Link } from 'routes';
import { getUserDisplayName } from 'utils/tools';

import ProfileAvatar from 'modules/users/components/ProfileAvatar';
import { QuestionAnswer } from 'modules/users/modules/profiles/types';
import { ProFeedback } from 'modules/marketing-pages/marketing-page.types';
import { PricingMenu } from './ProPricingMenuItemSection';
import StarRating from '@z-components/atoms/StarRating';
import ReadMore from '@z-components/atoms/ReadMore';
import ClientReviewCardNew from '@z-components/molecules/ClientReviewCardNew';

import styles from './ProCard.module.scss';
import ProMatchingCriteria from './ProMatchingCriteria';
import { createReviewMessageWithHeaderAndFooter } from 'utils/data-transform/review';
import ProCardLayout, { Slot, ClickableSection } from './ProCardLayout';
import PricingMenuItem from 'components/atoms/PricingMenuItem';

export interface ProCardProProfile {
  description: string;
  feedback?: ProFeedback;
  feedbackCount?: number;
  id: string;
  jobTypeProfile?: {
    description: string;
    questionAnswers?: Array<QuestionAnswer>;
    title: string;
  };
  latestFeedbackRelatedToJobType?: ProFeedback;
  location?: {
    title: string;
  };
  matchingCriteria?: Array<{
    answers: Array<{ en?: string; ja?: string }>;
    label: string;
    type: string;
  }>;
  pricingMenu?: PricingMenu;
  proProfile?: { description?: string };
  profile?: {
    avatar?: string;
    companyNameKanji?: string;
    fullNameKanji?: { first?: string; last?: string };
    isCompany?: boolean;
    languages?: { en?: boolean; ja?: boolean };
    title?: string;
  };
  rating?: number;
  shortId?: string;
  slug?: string;
  tier?: { _id: string; name: string };
}

export interface Props {
  applyMessage?: string;
  classes?: string;
  isExtendedSectionEnabled?: boolean;
  isReviewsQAEnabled: boolean;
  jobTypeId?: string;
  onGoogleAnalytics: (eventType: string) => void;
  pro: ProCardProProfile;
}

const ProCard: FC<Props> = ({
  applyMessage,
  isReviewsQAEnabled,
  classes,
  jobTypeId,
  onGoogleAnalytics,
  pro: {
    feedback,
    latestFeedbackRelatedToJobType,
    feedbackCount,
    id,
    jobTypeProfile,
    location,
    matchingCriteria,
    pricingMenu,
    profile,
    rating,
    shortId,
    slug,
    tier,
  },
  isExtendedSectionEnabled = true,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(true);

  const proDisplayName = useMemo(() => getUserDisplayName(language, profile), [language, profile]);
  const proLink = `${language === 'en' ? '/en' : ''}/profile/${slug ?? shortId ?? id}/pro/${
    jobTypeId ?? ''
  }`;

  const handleProAvatarClick = useCallback(() => onGoogleAnalytics('proAvatarClick'), [
    onGoogleAnalytics,
  ]);
  const handleUserDisplayNameClick = useCallback(() => onGoogleAnalytics('userDisplayNameClick'), [
    onGoogleAnalytics,
  ]);
  const handleReadMoreReviewClick = useCallback(() => onGoogleAnalytics('readMoreReviewClick'), [
    onGoogleAnalytics,
  ]);
  const handleWrapperClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      return router.push(proLink);
    },
    [proLink, router]
  );

  const formattedTime = dayjs(feedback?.time).format('YYYY/MM');

  const filteredQuestionAnswers = (jobTypeProfile?.questionAnswers ?? []).filter(
    (questionAnswer) => questionAnswer.answer
  );

  const handleToggle = useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  return (
    <ProCardLayout classes={classes} dataTestId="pro-card">
      <ClickableSection onClick={handleWrapperClick}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <Slot slotType="avatar" onClick={handleProAvatarClick}>
          <ProfileAvatar avatar={profile?.avatar} name={proDisplayName} />
        </Slot>
        <Slot slotType="userinfo">
          <label className={styles['name']}>
            <Link prefetch={false} route={proLink}>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <a onClick={handleUserDisplayNameClick}>{proDisplayName}</a>
            </Link>
          </label>
          <div className={styles['tier-rate-container']}>
            {tier && (
              <div className={styles['tier']}>
                <Image
                  alt={tier.name}
                  className="icon"
                  height={18}
                  unoptimized={true}
                  src={`/static/images/tiers/${tier._id}.svg`}
                  width={18}
                />
                <span className={styles['tier-name']}>{tier.name}</span>
              </div>
            )}
            {Number(rating?.toFixed(1)) > 0 && (
              <div className={styles['rating']}>
                {Number(rating?.toFixed(1))}
                <StarRating rating={Number(rating?.toFixed(1))} height={12} />(
                {feedbackCount?.toLocaleString()})
              </div>
            )}
          </div>
        </Slot>
        <Slot classes={styles.location} slotType="location">
          <Image
            alt=""
            height={24}
            unoptimized={true}
            src="/static/images/icons/Icon_Location.svg"
            width={24}
          />
          {location?.title}
        </Slot>
        {pricingMenu && Object.entries(pricingMenu).length > 0 && (
          <Slot slotType="pricingmenu" classes={styles['pricing-menu-container']}>
            <PricingMenuItem
              description={pricingMenu.description}
              hasPadding={false}
              priceRangeStart={pricingMenu.price}
              variant="flat"
            />
          </Slot>
        )}
        <Slot slotType="proprofile_short">
          {jobTypeProfile?.title && (
            <label className={styles['job-profile-title']}>{jobTypeProfile.title}</label>
          )}
        </Slot>
      </ClickableSection>
      {isExtendedSectionEnabled && (
        <Slot slotType="proprofile_extended">
          {(matchingCriteria && matchingCriteria.length > 0) && (
            <button onClick={handleToggle} type="button" className={styles['see-more-button']}>
              {isExpanded ? t('close') : t('users:profile.seeMore')}
            </button>
          )}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            onClick={handleWrapperClick}
            className={classNames({ [styles['collapsed-see-more']]: !isExpanded })}
          >
            {filteredQuestionAnswers.length > 0 && (
              <ReadMore language={language as 'en' | 'ja'}>
                {filteredQuestionAnswers.map(({ answer, question }) => (
                  <div key={question}>
                    <label className={styles['introduction-title']}>{question}</label>
                    <p className={styles['short-description']}>{answer}</p>
                  </div>
                ))}
              </ReadMore>
            )}
            {jobTypeProfile?.description && filteredQuestionAnswers.length > 0 && (
              <p className={styles['introduction-title']}>{t('users:profile.otherIntro')}</p>
            )}
            {jobTypeProfile?.description && (
              <div className={styles['description-section']}>
                <ReadMore language={language as 'en' | 'ja'} message={jobTypeProfile.description} />
              </div>
            )}
            {Boolean(rating) && !isReviewsQAEnabled && (
              <>
                <label className={styles['review-section-title']}>
                  {t('users:profile.verticalReview')}
                </label>
                <div className={styles['profile-content']}>
                  <StarRating
                    displayNumber={true}
                    rating={Number(rating?.toFixed(1))}
                    height={14}
                  />
                  {formattedTime}
                </div>
                {feedback && (
                  <div>
                    {feedback.message && (
                      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                      <div
                        className={styles['pro-review']}
                        data-test-id="read-more-review"
                        data-test-location-vs-tier="readMoreReview"
                        onClick={handleReadMoreReviewClick}
                      >
                        <ReadMore
                          language={language as 'en' | 'ja'}
                          message={createReviewMessageWithHeaderAndFooter(feedback, t)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {feedback && isReviewsQAEnabled && (
              <>
                <label className={styles['review-section-title']}>
                  {t('users:profile.verticalReview')}
                </label>
                <ClientReviewCardNew
                  inline={true}
                  compact={true}
                  rating={latestFeedbackRelatedToJobType?.rating}
                  time={latestFeedbackRelatedToJobType?.time}
                  title={latestFeedbackRelatedToJobType?.title}
                  ratingPersonality={latestFeedbackRelatedToJobType?.ratingPersonality}
                  ratingQuality={latestFeedbackRelatedToJobType?.ratingQuality}
                  message={latestFeedbackRelatedToJobType?.message}
                  nickname={latestFeedbackRelatedToJobType?.nickname}
                  questionAnswers={latestFeedbackRelatedToJobType?.questionAnswers}
                  age={latestFeedbackRelatedToJobType?.age}
                  pricing={latestFeedbackRelatedToJobType?.pricing}
                  prefecture={latestFeedbackRelatedToJobType?.prefectureName}
                />
              </>
            )}
            {(matchingCriteria && matchingCriteria.length > 0) && (
              <ReadMore language={language as 'en' | 'ja'}>
                <ProMatchingCriteria
                  language={language as 'en' | 'ja'}
                  matchingCriteria={matchingCriteria}
                />
              </ReadMore>
            )}
            {applyMessage && applyMessage.length > 0 && (
              <>
                <label className={styles['apply-message-section-title']}>
                  {t('marketingPages:proApplyMessageSectionTitle')}
                </label>
                <div className={styles['pro-apply-message']}>{applyMessage}</div>
              </>
            )}
          </div>
        </Slot>
      )}
    </ProCardLayout>
  );
};

export default ProCard;
export type { Props as ProCardProps };
